import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { budgetService } from '../services/budgetService';
import { useAppContext } from '../context/AppContext';
import MantleDataGrid, { gcell } from '../components/MantleDataGrid';
import { FilterPopover } from '../components/gridFilters';
import GridPager from '../components/GridPager';
import { usePeriodDrilldown } from '../hooks/usePeriodDrilldown';
import OpexBuildGrid from '../components/OpexBuildGrid';
import { periodLabel, PERIODS } from '../utils/financialPeriods';
import AiAssist from '../components/ai/AiAssist';

// =============================================================================
// BudgetGrid — the OPEX capture grid, on Glide Data Grid via MantleDataGrid
// (ported off ReactGrid; see MantleDataGrid for why). Drill-down: click the
// "Full Year" header → quarters → months. Month cells are editable on Manual
// lines only; P×Q lines open the build popup from the Method column. Edits are
// staged and written by the Save button in one batched call (it turns green
// while there are unsaved changes). Header ▾ filters
// GL Account / Cost Center / Method; the TOTAL row follows the filter.
// =============================================================================

// Fixed (non-period) columns: gl_account, cost_center, method — plus a Site
// column when the top bar is on "All sites".
const FIXED_BASE = 3;

// The grid loads a page, not a budget. Filters run in PostgreSQL and only the
// matching slice comes back; the header strip says how much was held back.
const PAGE_LIMIT = 100;

const fmtA = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Canvas theme accents (concrete colours — canvas can't read CSS vars).
const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.22)', baseFontStyle: '600 12px' };
const T_YEAR = { bgCell: 'rgba(100, 116, 139, 0.18)', baseFontStyle: '600 12px' };
const T_QTR = { bgCell: 'rgba(100, 116, 139, 0.10)', baseFontStyle: '600 12px' };
const T_CALC_CELL = { bgCell: 'rgba(100, 116, 139, 0.08)' };
const T_MANUAL = { textDark: '#b45309', bgCell: 'rgba(180, 83, 9, 0.10)' };
const T_PXQ = { textDark: '#1d4ed8', bgCell: 'rgba(29, 78, 216, 0.10)' };

// period_data carries { budget_amount } per period.
const sumPeriods = (row, ps) =>
  ps.reduce((acc, p) => acc + (row.period_data?.[p]?.budget_amount || 0), 0);

// onOpenBuild: optional callback(glLineId, rowMeta) — Dashboard opens the P*Q popup
const BudgetGrid = ({ onOpenBuild, module: moduleCode }) => {
  // fyStartMonth (Settings > Config Items) decides which calendar month period 1 is.
  // selectedRole/selectedSite drive the server-side row filter (role data access + site).
  const { selectedVersion, selectedRole, selectedSite, fyStartMonth } = useAppContext();
  const [rowsData, setRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [buildTarget, setBuildTarget] = useState(null); // { glLineId, meta } or null
  const [filterUI, setFilterUI] = useState(null);       // { colId, x, y, options }
  const [dirty, setDirty] = useState({});               // `${gl_line_id}:${period}` -> true
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Filtering moved to the server. `filters` keeps the same { text, values }
  // shape the FilterPopover already produced — only the place the work happens
  // changed, so the popover is untouched.
  const [filters, setFilters] = useState({});
  const [facets, setFacets] = useState({});             // { colId: [distinct values] }
  const [pageInfo, setPageInfo] = useState({ total: null, hasMore: false });
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(PAGE_LIMIT);

  // Changing page or filter refetches the rows. Staged edits are keyed by
  // gl_line_id, and handleSave skips any key whose row is no longer loaded
  // (`if (!r) continue`) — so without this guard, paging away from unsaved
  // cells drops them silently and the Save button still reads green. Ask.
  const dirtyCountRef = useRef(0);
  dirtyCountRef.current = Object.keys(dirty).length;
  const confirmDiscard = useCallback(() => {
    const n = dirtyCountRef.current;
    if (n === 0) return true;
    return window.confirm(
      `${n} unsaved cell${n === 1 ? '' : 's'} will be discarded when the rows reload. Save first, or continue and lose them?`
    );
  }, []);

  // Any filter change goes back to page 1 — a filter matching 20 rows, applied
  // while on page 3, would otherwise show nothing and look broken.
  const setFilter = useCallback((colId, f) => {
    if (!confirmDiscard()) return;
    setOffset(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (!f || (!f.text && !f.values)) delete next[colId];
      else next[colId] = f;
      return next;
    });
  }, [confirmDiscard]);
  const clearAll = useCallback(() => { if (!confirmDiscard()) return; setOffset(0); setFilters({}); }, [confirmDiscard]);
  const activeIds = useMemo(() => Object.keys(filters), [filters]);


  const loadGrid = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await budgetService.getGridData(selectedVersion, moduleCode, {
        filters, limit, offset,
      });
      const gridData = Array.isArray(result) ? result : result.data;
      if (gridData) setRowsData(gridData);
      // `facets` are the distinct values across the WHOLE permission-scoped
      // set, not just this page — without them the filter checklist could only
      // ever offer values from the rows already on screen. The server only
      // sends them on the unfiltered load (they do not change as columns are
      // filtered), so keep what we have when they are absent.
      if (result?.facets) setFacets(result.facets);
      setPageInfo({ total: result?.total ?? null, hasMore: !!result?.hasMore });
      // The rows these flags pointed at are gone; leaving them set would keep
      // Save green over edits that no longer exist.
      setDirty({});
    } catch (error) {
      console.error('Failed to load grid:', error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersion, selectedRole, selectedSite, moduleCode, filters, limit, offset]);

  // Debounced: the filter popover's contains-box would otherwise fire a query
  // per keystroke.
  useEffect(() => {
    const t = setTimeout(loadGrid, 250);
    return () => clearTimeout(t);
  }, [loadGrid]);

  // "All sites" in the top bar reveals a Site column (rows span every site).
  const allSites = !selectedSite;
  const FIXED = allSites ? FIXED_BASE + 1 : FIXED_BASE;

  // ---- Per-column filters on the fixed columns --------------------------------
  // The rows arriving from the server are ALREADY filtered, so `filtered` is
  // just what came back. The alias is kept so the rest of the file — cell
  // rendering, editing, the TOTAL row — reads the same as before.
  const filtered = rowsData;

  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  // ---- Drill-down columns ------------------------------------------------------
  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  const gcols = useMemo(() => {
    const dot = (id, title) => (activeIds.includes(id) ? `${title} ●` : title);
    const base = [
      ...(allSites
        ? [{ id: 'site', title: dot('site', 'Site'), width: 130, grow: 0.5, hasMenu: true }]
        : []),
      { id: 'gl_account', title: dot('gl_account', 'GL Account'), width: 240, grow: 1, hasMenu: true },
      { id: 'cost_center', title: dot('cost_center', 'Cost Center'), width: 100, hasMenu: true },
      { id: 'method', title: dot('method', 'Method'), width: 96, hasMenu: true },
    ];
    const dyn = visibleCols.map((col) => {
      if (col.kind === 'year') return { id: 'YEAR', title: `${yearExpanded ? '▾' : '▸'} Full Year`, width: 130 };
      if (col.kind === 'quarter') return { id: `Q${col.q}`, title: `${expandedQuarters[col.q] ? '▾' : '▸'} Q${col.q}`, width: 110 };
      return { id: `P${col.period}`, title: periodLabel(col.period, fyStartMonth), width: 88 };
    });
    return [...base, ...dyn];
  }, [visibleCols, yearExpanded, expandedQuarters, fyStartMonth, activeIds, allSites]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  // Column totals over the VISIBLE (filtered) rows.
  const colTotals = useMemo(() => visibleCols.map((c) => {
    const ps = c.kind === 'month' ? [c.period] : c.periods;
    return filtered.reduce((acc, row) => acc + sumPeriods(row, ps), 0);
  }), [visibleCols, filtered]);

  const rowCount = filtered.length + 1; // + TOTAL row

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];

    if (row === filtered.length) { // TOTAL row
      if (id === 'gl_account') {
        return gcell.text(activeIds.length ? 'TOTAL (filtered)' : 'TOTAL', { readonly: true, theme: T_TOTAL });
      }
      if (col >= FIXED) {
        const t = colTotals[col - FIXED] || 0;
        return gcell.number(t, { readonly: true, display: fmtA(t), theme: T_TOTAL });
      }
      return gcell.text('', { readonly: true, theme: T_TOTAL });
    }

    const r = filtered[row];
    if (!r) return gcell.text('', { readonly: true });

    if (id === 'site') return gcell.text(r.site_name || r.site_code || 'All sites', { readonly: true });
    if (id === 'gl_account') return gcell.text(`${r.account_code} - ${r.account_description}`, { readonly: true });
    if (id === 'cost_center') return gcell.text(r.cost_center_code || '', { readonly: true });
    if (id === 'method') {
      return gcell.text(r.is_calculated ? 'P×Q ▸' : 'Manual', {
        readonly: true, theme: r.is_calculated ? T_PXQ : T_MANUAL,
      });
    }

    const d = visibleCols[col - FIXED];
    if (!d) return gcell.text('', { readonly: true });
    if (d.kind === 'month') {
      const v = r.period_data?.[d.period]?.budget_amount || 0;
      // Manual lines are typed directly; P×Q lines are computed in the build popup.
      return r.is_calculated
        ? gcell.number(v, { readonly: true, display: fmtA(v), theme: T_CALC_CELL })
        : gcell.number(v, { display: fmtA(v) });
    }
    const v = sumPeriods(r, d.periods);
    return gcell.number(v, { readonly: true, display: fmtA(v), theme: d.kind === 'year' ? T_YEAR : T_QTR });
  }, [colIds, filtered, colTotals, visibleCols, activeIds, FIXED]);

  // ---- Explicit save: edits are staged until the user hits Save ----------------
  // (Previously these auto-saved on a 400ms debounce. Every other capture screen
  // in the app uses a Save button, so this one does too.)
  const rowsRef = useRef([]);
  rowsRef.current = rowsData;

  const onCellEdited = useCallback(([col, row], newVal) => {
    if (row >= filteredRef.current.length) return; // TOTAL row
    const id = colIds[col];
    if (!/^P\d+$/.test(id)) return; // only month cells are editable
    const r = filteredRef.current[row];
    if (!r || r.is_calculated) return;
    const period = parseInt(id.slice(1), 10);
    const value = Number(newVal.data) || 0;

    setRowsData((prev) => prev.map((x) => (x.gl_line_id === r.gl_line_id
      ? {
          ...x,
          period_data: {
            ...x.period_data,
            [period]: { ...(x.period_data?.[period] || {}), budget_amount: value },
          },
        }
      : x)));
    // Functional update: a pasted range arrives as one onCellEdited per cell in
    // a single tick, so seeding from the `dirty` closure would keep only the last.
    setDirty((prev) => ({ ...prev, [`${r.gl_line_id}:${period}`]: true }));
  }, [colIds]);

  const hasChanges = Object.keys(dirty).length > 0;

  // ---- AI assist ----------------------------------------------------------
  //
  // The assistant knows nothing about period_data — this page teaches it how to
  // read and write its own numbers, which is what lets one component serve OPEX,
  // BOM, rates and prices without knowing anything about any of them.
  //
  // Calculated (P×Q) lines are deliberately absent: they are an output of a
  // build, and escalating one would be overwritten the next time it recalculated
  // while looking like it had taken.
  const aiFields = useMemo(() => ([{
    key: 'budget_amount',
    label: 'budgeted amount',
    periods: () => PERIODS,
    get: (row, period) => row.period_data?.[period]?.budget_amount || 0,
  }]), []);

  // The assistant hands back CHANGES, not rows: this page owns its state and its
  // definition of dirty, so nothing about saving changes because an edit came
  // from a sentence rather than a keystroke. The Save button, its validation and
  // its role checks are untouched — which is the point.
  const applyAiChanges = useCallback((changes) => {
    const byLine = new Map();
    changes.forEach((c) => {
      if (!byLine.has(c.row.gl_line_id)) byLine.set(c.row.gl_line_id, {});
      byLine.get(c.row.gl_line_id)[c.slot] = c.to;
    });

    setRowsData((prev) => prev.map((r) => {
      const edits = byLine.get(r.gl_line_id);
      if (!edits) return r;
      const period_data = { ...r.period_data };
      Object.entries(edits).forEach(([p, v]) => {
        period_data[p] = { ...(period_data[p] || {}), budget_amount: v };
      });
      return { ...r, period_data };
    }));

    setDirty((prev) => {
      const next = { ...prev };
      changes.forEach((c) => { next[`${c.row.gl_line_id}:${c.slot}`] = true; });
      return next;
    });
  }, []);

  // Edits are no longer auto-persisted, so warn before the tab is closed with
  // unsaved cells still pending.
  useEffect(() => {
    if (!hasChanges) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasChanges]);

  const handleSave = useCallback(async () => {
    const updates = [];
    for (const key of Object.keys(dirty)) {
      // gl_line_id is a UUID (no colons), so the last ':' splits it cleanly.
      const sep = key.lastIndexOf(':');
      const glLineId = key.slice(0, sep);
      const period = Number(key.slice(sep + 1));
      const r = rowsRef.current.find((x) => x.gl_line_id === glLineId);
      if (!r) continue;
      updates.push({
        gl_line_id: glLineId,
        financial_period: period,
        budget_amount: r.period_data?.[period]?.budget_amount || 0,
      });
    }
    if (updates.length === 0) { setDirty({}); return; }

    setIsSaving(true);
    setSaveError(null);
    try {
      await budgetService.updateCells(updates);
      setDirty({});
    } catch (err) {
      console.error('Failed to save cell updates:', err.response?.data || err.message);
      setSaveError(err.response?.data?.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  }, [dirty]);

  // Method column click on a P×Q line → open the build popup.
  const onCellClicked = useCallback(([col, row]) => {
    if (row >= filteredRef.current.length) return;
    if (colIds[col] !== 'method') return;
    const r = filteredRef.current[row];
    if (!r?.is_calculated) return;
    setBuildTarget({
      glLineId: r.gl_line_id,
      meta: {
        account_code: r.account_code,
        account_description: r.account_description,
        cost_center_code: r.cost_center_code,
      },
    });
    onOpenBuild?.(r.gl_line_id, r); // still fire the optional parent hook
  }, [colIds, onOpenBuild]);

  // Header CLICK drives the drill-down (Full Year / quarter toggles). The header
  // ▾ MENU (filter) is separate and only exists on the fixed columns.
  const onHeaderClicked = useCallback((colIndex) => {
    const id = colIds[colIndex];
    if (id === 'YEAR') toggleYear();
    else if (/^Q\d+$/.test(id)) toggleQuarter(Number(id.slice(1)));
  }, [colIds, toggleYear, toggleQuarter]);

  const onHeaderMenu = useCallback((colIndex, bounds) => {
    const colId = colIds[colIndex];
    // Options come from the server's facets — the distinct values across every
    // row the user may see, not just the page in front of them. Falling back to
    // the loaded rows keeps the menu usable if facets are unavailable.
    const options = facets[colId]
      || [...new Set(rowsData.map((r) => String(r[colId] ?? '')))].sort();
    if (!options || options.length === 0) return;
    setFilterUI({
      colId,
      options,
      x: bounds ? bounds.x : 200,
      y: bounds ? bounds.y + bounds.height : 200,
    });
  }, [colIds, facets, rowsData]);

  const toolbar = useMemo(() => (
    <>
      {/* First in the strip, so it reads as "start here" rather than as one more
          grid control. Calculated lines are excluded from what it can touch. */}
      <AiAssist
        context={{
          module: moduleCode,
          pageLabel: 'OPEX budget capture',
          versionLabel: selectedVersion,
          siteLabel: selectedSite || 'All sites',
        }}
        rows={rowsData.filter((r) => !r.is_calculated)}
        totalCount={pageInfo.total}
        hasMore={pageInfo.hasMore}
        fields={aiFields}
        onApply={applyAiChanges}
        disabled={isLoading || !selectedVersion}
      />
      {activeIds.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="px-3 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
        >
          Clear filters ({activeIds.length})
        </button>
      )}
      <button
        type="button"
        onClick={expandAll}
        className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        Expand all
      </button>
      <button
        type="button"
        onClick={collapseAll}
        className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        Collapse all
      </button>
      {saveError && <span className="text-red-600 dark:text-red-400">{saveError}</span>}
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving || !hasChanges}
        className={`px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          hasChanges
            ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
        }`}
      >
        {isSaving ? 'Saving…' : hasChanges ? `Save (${Object.keys(dirty).length})` : 'Save'}
      </button>
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [activeIds.length, clearAll, hasChanges, isSaving, handleSave, saveError, dirty,
      moduleCode, selectedVersion, selectedSite, rowsData, pageInfo, aiFields, applyAiChanges, isLoading]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div className="h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
        {isLoading ? (
          <div className="p-4 text-xs text-slate-400">Loading…</div>
        ) : rowsData.length === 0 && activeIds.length === 0 && offset === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No GL lines for this version/site — set them up in Budget Setup.
          </div>
        ) : (
          <>
            {/* The card is h-full (a DEFINITE height) and the grid sits in a
                flex-1 min-h-0 child, so the shrink-0 pager below always keeps
                its own space. This only works with a definite height: under the
                old max-h-full card the height was content-driven, flex-1's zero
                basis contributed nothing, and the canvas overflowed straight
                over the pager. Same pattern as Volumes / Master Data / Customers. */}
            <div className="flex-1 min-h-0">
            <MantleDataGrid
              gridId="opex"
              fitHeight
              columns={gcols}
              rowCount={rowCount}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              onHeaderClicked={onHeaderClicked}
              onHeaderMenuClick={onHeaderMenu}
              freezeColumns={FIXED}
              hint="Click Full Year → quarters → months to drill in. Click a P×Q Method to edit its build. Header ▾ filters — filtering runs on the server, so it searches every line, not just this page. Edit Manual month cells (paste/fill works), then press Save."
              toolbar={toolbar}
            />
            </div>
            <GridPager
              offset={offset}
              limit={limit}
              rowCount={filtered.length}
              total={pageInfo.total}
              hasMore={pageInfo.hasMore}
              loading={isLoading}
              onOffset={(n) => { if (confirmDiscard()) setOffset(n); }}
              onLimit={(n) => { if (confirmDiscard()) { setLimit(n); setOffset(0); } }}
            />
          </>
        )}
      </div>

      {/* Column filter popover */}
      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {buildTarget && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 32,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setBuildTarget(null); }}
        >
          <div
            style={{
              width: '92%', maxWidth: 1600, height: '90%',
              background: 'var(--grid-zebra-even)', borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 24px 70px rgba(0,0,0,0.4)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <OpexBuildGrid
              glLineId={buildTarget.glLineId}
              lineMeta={buildTarget.meta}
              onClose={() => setBuildTarget(null)}
              onSaved={loadGrid}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetGrid;
